import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faBuilding, faCalendarAlt, faUserFriends } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { DateTime } from 'luxon';
import Notifications from '../SharedView/Notifications'; // adjust path if needed
import Scheduling from './Bookings/Scheduling/Scheduling'; // adjust path if needed
import PendingClientRequests from './ClientRequests/PendingClientRequests';
import './home.css';

function Home() {
  const [stats, setStats] = useState({
    clients: 0,
    properties: 0,
    bookings: 0,
    teamMembers: 0,
  });
  const [error, setError] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [userRole, setUserRole] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch the first name and user role from localStorage
    const storedFirstName = localStorage.getItem('firstName');
    const storedUserRole = localStorage.getItem('userRole');

    setFirstName(storedFirstName);
    setUserRole(storedUserRole);

    // Redirect based on user role
    if (storedUserRole === 'warehouse') {
      navigate('/laundry');
      return;
    }

    if (storedUserRole === 'driver') {
      navigate('/drivers');
      return;
    }

    if (storedUserRole === 'client') {
      navigate('/client/home');
      return;
    }

    // Fetch the count statistics for Clients, Properties, Bookings, Team Members
    const fetchStats = async () => {
      try {
        const [clientsRes, propertiesRes, bookingsRes, teamRes] = await Promise.all([
          axios.get('/api/clients/count'),
          axios.get('/api/properties/count'),
          axios.get('/api/bookings/count'),
          axios.get('/api/team/count'),
        ]);

        setStats({
          clients: clientsRes.data.count || 0,
          properties: propertiesRes.data.count || 0,
          bookings: bookingsRes.data.count || 0,
          teamMembers: teamRes.data.count || 0,
        });
      } catch (error) {
        console.error('Error fetching statistics:', error);
        setError('There was an issue fetching the data. Please try again later.');
      }
    };

    fetchStats();
  }, [navigate]);

  // Use Luxon with Dubai time zone
  const todayDubai = DateTime.now().setZone('Asia/Dubai');
  const today = todayDubai.toISODate();              // for Scheduling (YYYY-MM-DD)

  return (
    <div className="home-dashboard">
      <h1 className="home-dashboard-title">
        Welcome {firstName ? firstName : 'Admin'}! Have a good day ahead!
      </h1>
      <p className="home-dashboard-subtitle">
        Let&apos;s manage our clients, properties, bookings, and team members from here.
      </p>

      {error ? (
        <div className="error-message">{error}</div>
      ) : (
        <div className="home-dashboard-stats-cards">
          <Link to="/clients" className="home-dashboard-card">
            <div className="home-dashboard-card-icon">
              <FontAwesomeIcon icon={faUsers} />
            </div>
            <div className="home-dashboard-card-info">
              <h3 className="home-dashboard-card-info-title">{stats.clients}</h3>
              <p className="home-dashboard-card-info-label">Clients</p>
            </div>
          </Link>

          <Link to="/properties" className="home-dashboard-card">
            <div className="home-dashboard-card-icon">
              <FontAwesomeIcon icon={faBuilding} />
            </div>
            <div className="home-dashboard-card-info">
              <h3 className="home-dashboard-card-info-title">{stats.properties}</h3>
              <p className="home-dashboard-card-info-label">Properties</p>
            </div>
          </Link>

          <Link to="/bookings" className="home-dashboard-card">
            <div className="home-dashboard-card-icon">
              <FontAwesomeIcon icon={faCalendarAlt} />
            </div>
            <div className="home-dashboard-card-info">
              <h3 className="home-dashboard-card-info-title">{stats.bookings}</h3>
              <p className="home-dashboard-card-info-label">Bookings</p>
            </div>
          </Link>

          <Link to="/team" className="home-dashboard-card">
            <div className="home-dashboard-card-icon">
              <FontAwesomeIcon icon={faUserFriends} />
            </div>
            <div className="home-dashboard-card-info">
              <h3 className="home-dashboard-card-info-title">{stats.teamMembers}</h3>
              <p className="home-dashboard-card-info-label">Team Members</p>
            </div>
          </Link>
        </div>
      )}

      {/* Pending client requests — admins/managers/supervisors approve or reject */}
      {(userRole === 'admin' || userRole === 'manager' || userRole === 'supervisor') && (
        <PendingClientRequests />
      )}

      {/* Notifications always visible for Home users */}
      <Notifications />

      {/* Show today's bookings & scheduling only for admins and managers */}
      {(userRole === 'admin' || userRole === 'manager' || userRole === 'supervisor') && (
        <div className="home-scheduling-section">
          <Scheduling customDate={today} />
        </div>
      )}
    </div>
  );
}

export default Home;